// Catálogo comercial inicial de OWA.
//
// Los precios y las políticas de cancelación se guardan por actividad para
// evitar reglas genéricas que no coincidan con las condiciones del proveedor.
// La comisión de OWA nunca se incluye en este archivo público.

import { additionalActivities } from './additionalActivityCatalog';

const sharedBoatImages = {
  yacht: 'https://images.unsplash.com/photo-1571328532572-cb7899a8be7e?auto=format&fit=crop&w=900&q=80',
  yachtWide: 'https://images.unsplash.com/photo-1571328532572-cb7899a8be7e?auto=format&fit=crop&w=1400&q=80',
  ocean: 'https://images.unsplash.com/photo-1526621480041-c4e2027dfe69?auto=format&fit=crop&w=1400&q=80',
  catamaran: 'https://images.unsplash.com/photo-1564555449603-b0eefe1b9094?auto=format&fit=crop&w=900&q=80',
  catamaranWide: 'https://images.unsplash.com/photo-1564555449603-b0eefe1b9094?auto=format&fit=crop&w=1400&q=80',
};

const jetSkiImages = {
  card: 'https://images.unsplash.com/photo-1564633351631-e85bd59a91af?auto=format&fit=crop&w=900&q=80',
  wide: 'https://images.unsplash.com/photo-1564633351631-e85bd59a91af?auto=format&fit=crop&w=1400&q=80',
};

const yachtTourCancellation = {
  verifiedOn: '2026-08-10',
  noticeHours: 48,
  short: 'Cancelación gratuita hasta 48 horas antes.',
  details: 'Cancela con al menos 48 horas de antelación para recibir un reembolso completo.',
  sourceUrl: '',
};

const jetSkiCancellation = {
  verifiedOn: '2026-08-10',
  noticeHours: 24,
  short: 'Cancelación gratuita hasta 24 horas antes.',
  details: 'Las cancelaciones comunicadas con al menos 24 horas de antelación reciben un reembolso completo. Si el tour se cancela por meteorología o estado del mar, se reprograma o se reembolsa íntegramente.',
  sourceUrl: '',
};

const jetSkiBase = {
  location: 'Pasito Blanco, Gran Canaria',
  destination: 'gran-canaria',
  category: 'nauticas',
  rating: null,
  reviews: 0,
  featured: true,
  image: jetSkiImages.card,
  gallery: [jetSkiImages.wide],
  languages: 'ES / EN',
  groupSize: 'Hasta 2 pers. por moto',
  insurance: 'Incluido',
  meetingPoint: 'Puerto de Pasito Blanco, San Bartolomé de Tirajana',
  pickup: 'Recogida gratuita en hoteles entre Maspalomas y Puerto Rico.',
  provider: {
    id: 'gc-jetski-partner',
    name: 'Socio local de moto de agua',
    activityUrl: '',
  },
  cancellationPolicy: jetSkiCancellation,
  restrictions: [
    'Edad mínima para conducir: 16 años con autorización del padre, madre o tutor legal.',
    'Edad mínima como acompañante: 7 años.',
    'No recomendado durante el embarazo ni para personas con problemas cardíacos o lesiones de espalda o cuello.',
    'Es necesario presentarse al menos 15 minutos antes.',
  ],
  ticketTypes: [
    { id: 'single_jet_ski', label: 'Moto individual', price: null, min: 0, max: 6, default: 1, seats: 1 },
    { id: 'double_jet_ski', label: 'Moto doble', price: null, min: 0, max: 6, default: 0, seats: 2 },
  ],
  priceUnit: 'moto de agua',
  paymentCurrency: 'EUR',
  bookingEnabled: true,
};

function jetSkiActivity({
  id,
  title,
  price,
  duration,
  shortDescription,
  description,
  included,
  notIncluded,
}) {
  return {
    ...jetSkiBase,
    id,
    title,
    price,
    originalPrice: null,
    duration,
    shortDescription,
    description,
    included,
    notIncluded,
    ticketTypes: jetSkiBase.ticketTypes.map((ticket) => ({ ...ticket, price })),
    timeSlots: ['Horario a confirmar'],
  };
}

function legacyExperience({
  id,
  title,
  location,
  destination,
  category,
  price,
  originalPrice = null,
  duration,
  rating,
  reviews,
  featured = false,
  image,
  gallery,
  shortDescription,
  description,
  included,
  notIncluded,
  meetingPoint,
  pickup = 'Confirmaremos el punto exacto y la logistica al cerrar la reserva.',
  groupSize = 'Grupo reducido',
  insurance = 'Incluido',
  languages = 'ES / EN',
  providerName = 'OWA Partner',
  providerUrl = '',
  cancellationNoticeHours = 24,
  cancellationShort = 'Cancelacion gratuita hasta 24 horas antes.',
  cancellationDetails = 'Consulta las condiciones finales con el operador antes de confirmar la reserva.',
  restrictions = [],
  ticketTypes,
  timeSlots = ['Horario a confirmar'],
  priceUnit = 'persona',
  paymentCurrency = 'EUR',
  bookingEnabled = true,
}) {
  const normalizedTicketTypes = ticketTypes || [
    {
      id: 'adult',
      label: 'Adultos',
      price,
      min: 1,
      max: 12,
      default: 2,
      seats: 1,
    },
  ];

  return {
    id,
    title,
    location,
    destination,
    category,
    price,
    originalPrice,
    duration,
    rating,
    reviews,
    featured,
    image,
    gallery,
    shortDescription,
    description,
    included,
    notIncluded,
    meetingPoint,
    pickup,
    groupSize,
    insurance,
    languages,
    provider: {
      id,
      name: providerName,
      activityUrl: providerUrl,
    },
    cancellationPolicy: {
      verifiedOn: '2026-08-11',
      noticeHours: cancellationNoticeHours,
      short: cancellationShort,
      details: cancellationDetails,
      sourceUrl: providerUrl,
    },
    restrictions,
    ticketTypes: normalizedTicketTypes,
    timeSlots,
    priceUnit,
    paymentCurrency,
    bookingEnabled,
  };
}

export const activities = [
  {
    id: 'gc-yate-premium-compartido',
    title: 'Excursión Compartida en Yate Premium',
    location: 'Puerto Rico, Gran Canaria',
    destination: 'gran-canaria',
    category: 'nauticas',
    price: 79.21,
    originalPrice: null,
    duration: '4 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: '/media/gc-yate/shared-01.jpg',
    gallery: [
      '/media/gc-yate/shared-01.jpg',
      '/media/gc-yate/shared-02.jpg',
      '/media/gc-yate/shared-03.jpg',
      '/media/gc-yate/shared-04.jpg',
      '/media/gc-yate/shared-05.jpg',
    ],
    shortDescription: 'Tour compartido para un máximo de 12 pasajeros con aperitivos, barra libre, snorkel y paddle surf.',
    description: 'La experiencia comienza con la recogida, previamente reservada, en alojamientos del sur o directamente en Puerto Escala (Puerto Rico). El yate navega hacia la costa de Mogán y fondea en Cala del Perchel o en la bahía de Mogán para disfrutar de entre 90 y 120 minutos de baño, snorkel y paddle surf mientras la tripulación prepara el aperitivo. Durante el regreso se contemplan la playa de Tauro y los acantilados de Tiritaña. La ruta puede variar según las condiciones del mar.',
    highlights: [
      'Grupo compartido reducido, máximo 12 pasajeros',
      'Parada de baño de entre 90 y 120 minutos',
      'Navegación por la costa de Mogán, Tauro y Tiritaña',
      'Música, aperitivo y bebidas durante la experiencia',
    ],
    included: [
      'Recogida y regreso en alojamientos del sur con reserva previa',
      'Capitán y guía',
      'Copa de cava de bienvenida',
      'Barra libre de cerveza, refrescos, sangría, agua y zumos',
      'Surtido de aperitivos',
      'Equipo de snorkel, 2 tablas de paddle surf y plataforma flotante',
      'Seguro, impuestos y tasas',
    ],
    notIncluded: [
      'Toallas',
      'Cócteles, vino, cavas y combinados adicionales',
      'Jet ski, sea scooter y parasailing opcionales',
    ],
    meetingPoint: 'Puerto Escala, Puerto Rico, Gran Canaria',
    pickup: 'Recogida en alojamientos y hoteles del sur con reserva anticipada.',
    groupSize: 'Máx. 12 pers.',
    insurance: 'Incluido',
    languages: 'ES / EN',
    restrictions: [
      'No se permiten mascotas, alcohol o drogas ni desnudez.',
      'Se recomienda medicación preventiva a las personas propensas al mareo.',
    ],
    provider: {
      id: 'infinity-boat-gc',
      name: 'Socio local de excursiones en barco',
      activityUrl: '',
    },
    cancellationPolicy: {
      ...yachtTourCancellation,
      sourceUrl: '',
    },
    ticketTypes: [
      { id: 'adult', label: 'Adultos', price: 79.21, min: 0, max: 12, default: 2 },
      { id: 'child', label: 'Niños (1-10)', price: 55, min: 0, max: 12, default: 0 },
    ],
    timeSlots: ['09:30', '13:30'],
    priceUnit: 'persona',
    paymentCurrency: 'EUR',
    bookingEnabled: true,
  },
  {
    id: 'gc-catamaran-paradise-compartido',
    title: 'Crucero Compartido en Catamarán Paradise',
    location: 'Pasito Blanco, Gran Canaria',
    destination: 'gran-canaria',
    category: 'nauticas',
    price: 73.93,
    originalPrice: null,
    duration: '4 h 30 min',
    rating: null,
    reviews: 0,
    featured: true,
    image: sharedBoatImages.catamaran,
    gallery: [sharedBoatImages.catamaranWide, sharedBoatImages.ocean],
    shortDescription: 'Crucero por la costa sur con almuerzo, barra libre, snorkel, paddle surf y traslado.',
    description: 'Excursión compartida desde Pasito Blanco a bordo del Paradise Cat. Navega por la costa sur, realiza una parada de baño de entre 90 y 120 minutos y disfruta de snorkel y paddle surf. El avistamiento de delfines puede ocurrir, pero no está garantizado.',
    included: [
      'Recogida y regreso desde puntos seleccionados del sur con reserva previa',
      'Capitán y marinero',
      'Copa de cava de bienvenida',
      'Barra libre de cerveza, sangría, refrescos, agua y zumos',
      'Almuerzo',
      'Equipo de snorkel y tablas de paddle surf',
      'Seguro, impuestos y tasas',
    ],
    notIncluded: [
      'Toallas',
      'Cócteles, vino y combinados adicionales',
      'Jet ski, crazy sofa, sea scooter y parasailing opcionales',
    ],
    meetingPoint: 'Puerto de Pasito Blanco, Meloneras, Gran Canaria',
    pickup: 'Recogida en puntos seleccionados de las zonas hoteleras del sur con reserva anticipada.',
    groupSize: 'Máx. 45 pers.',
    insurance: 'Incluido',
    languages: 'ES / EN',
    restrictions: [
      'Actividad no apta para personas en silla de ruedas.',
      'No se permiten mascotas, armas u objetos punzantes, alcohol o drogas ni desnudez.',
    ],
    provider: {
      id: 'infinity-boat-gc',
      name: 'Socio local de excursiones en barco',
      activityUrl: '',
    },
    cancellationPolicy: {
      ...yachtTourCancellation,
      sourceUrl: '',
    },
    ticketTypes: [
      { id: 'adult', label: 'Adultos', price: 73.93, min: 0, max: 45, default: 2 },
      { id: 'child', label: 'Niños (3-10)', price: 50, min: 0, max: 45, default: 0 },
    ],
    timeSlots: ['10:30'],
    priceUnit: 'persona',
    paymentCurrency: 'EUR',
    bookingEnabled: true,
  },
  {
    id: 'gc-yate-sport-privado',
    title: 'Yate Sport Privado — 4 horas',
    location: 'Puerto Rico, Gran Canaria',
    destination: 'gran-canaria',
    category: 'nauticas',
    price: 600,
    originalPrice: null,
    duration: '4 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: '/media/gc-yate/sport-01.jpg',
    gallery: [
      '/media/gc-yate/sport-01.jpg',
      '/media/gc-yate/sport-02.jpg',
      '/media/gc-yate/sport-03.jpg',
      '/media/gc-yate/sport-04.jpg',
      '/media/gc-yate/sport-05.jpg',
    ],
    shortDescription: 'Salida privada en yate Sport para hasta 10 personas: baño, snorkel, paddle surf y aperitivo en la costa de Mogán.',
    description: 'Salida privada desde Puerto Escala (Puerto Rico) para descubrir la costa de Mogán con tu grupo, hasta 10 pasajeros. Navegamos hacia Cala del Perchel o la bahía de Mogán, donde podréis bañaros, hacer snorkel y paddle surf mientras nuestra tripulación prepara un aperitivo. Incluye bebidas y traslado desde alojamientos del sur con reserva previa; el itinerario puede variar según las condiciones del mar.',
    highlights: [
      'Yate Sport en exclusiva para tu grupo',
      'Hasta 8 personas incluidas y 2 pasajeros extra',
      'Parada para baño, snorkel y paddle surf',
      'Opción de ampliar la experiencia por horas',
    ],
    included: [
      'Recogida y regreso en alojamientos del sur con reserva anticipada',
      'Yate privado con capitán',
      'Copa de cava de bienvenida',
      'Barra libre de cerveza, refrescos, sangría, agua y zumos',
      'Surtido de aperitivos',
      'Equipo de snorkel, tabla de paddle surf y plataforma flotante',
      'Seguro, impuestos y tasas',
    ],
    notIncluded: [
      'Toallas',
      'Cócteles, cavas, vino y combinados adicionales',
      'Jet ski, crazy sofa, sea scooter y parasailing opcionales',
      'Hora extra: 150 €',
    ],
    meetingPoint: 'Puerto Escala, Puerto Rico, Gran Canaria',
    pickup: 'Recogida y regreso en alojamientos del sur con reserva previa.',
    groupSize: 'Hasta 10 pers.',
    insurance: 'Incluido',
    languages: 'ES / EN',
    restrictions: [
      'Actividad no apta para personas en silla de ruedas.',
      'Lleva gafas de sol, toalla y protector solar.',
      'No se permiten mascotas, armas u objetos punzantes, alcohol o drogas ni desnudez.',
      'Se recomienda medicación preventiva a las personas propensas al mareo.',
      'La actividad está sujeta a disponibilidad y condiciones meteorológicas.',
    ],
    provider: { id: 'gc-yate-sport', name: 'OWA', activityUrl: '' },
    cancellationPolicy: { ...yachtTourCancellation, sourceUrl: '' },
    ticketTypes: [
      { id: 'base_group', label: 'Grupo hasta 8 personas', price: 600, min: 1, max: 1, default: 1 },
      { id: 'extra_guest', label: 'Pasajero extra (máx. 2)', price: 45, min: 0, max: 2, default: 0 },
    ],
    timeSlots: ['10:30', '14:30', 'Sunset (verano)'],
    priceUnit: 'grupo privado',
    paymentCurrency: 'EUR',
    bookingEnabled: true,
  },
  {
    id: 'gc-yate-premium-privado',
    title: 'Yate Privado Premium — 4 horas',
    location: 'Puerto Rico, Gran Canaria',
    destination: 'gran-canaria',
    category: 'nauticas',
    price: 680,
    originalPrice: null,
    duration: '4 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: '/media/gc-yate/private-02.jpg',
    gallery: [
      '/media/gc-yate/private-02.jpg',
      '/media/gc-yate/private-01.jpg',
      '/media/gc-yate/private-03.jpg',
      '/media/gc-yate/private-04.jpg',
    ],
    shortDescription: 'Yate premium en exclusiva para tu grupo (hasta 12): baño, snorkel, paddle surf y aperitivos por la costa de Mogán.',
    description: 'Experiencia exclusiva en nuestro yate premium para navegar desde Puerto Rico hacia la costa de Mogán. Tu grupo dispone del barco en privado y podréis disfrutar de una parada para baño, snorkel y paddle surf mientras la tripulación sirve aperitivos y bebidas. Incluye hasta 8 personas, permite añadir hasta 4 pasajeros y puede ampliarse por horas. El itinerario puede variar según las condiciones del mar.',
    highlights: [
      'Yate premium en exclusiva para tu grupo',
      'Hasta 8 personas incluidas y 4 pasajeros extra',
      'Ruta por Cala del Perchel, Mogán, Tauro y Tiritaña',
      'Posibilidad de añadir horas a la experiencia',
    ],
    included: [
      'Recogida y regreso en alojamientos del sur con reserva anticipada',
      'Yate privado con capitán y guía',
      'Copa de cava de bienvenida',
      'Barra libre de cerveza, refrescos, sangría, agua y zumos',
      'Surtido de aperitivos',
      'Equipo de snorkel, 2 tablas de paddle surf y plataforma flotante',
      'Seguro, impuestos y tasas',
    ],
    notIncluded: [
      'Toallas',
      'Cócteles, cavas, vino y combinados adicionales',
      'Jet ski, sea scooter y parasailing opcionales',
      'Hora extra: 170 €',
    ],
    meetingPoint: 'Puerto Escala, Puerto Rico, Gran Canaria',
    pickup: 'Recogida y regreso en alojamientos y hoteles del sur con reserva anticipada.',
    groupSize: 'Hasta 12 pers.',
    insurance: 'Incluido',
    languages: 'ES / EN',
    restrictions: [
      'Lleva gafas de sol, toalla y protector solar.',
      'No se permiten mascotas, alcohol o drogas ni desnudez.',
      'Se recomienda medicación preventiva a las personas propensas al mareo.',
      'La actividad está sujeta a disponibilidad y condiciones meteorológicas.',
    ],
    provider: { id: 'gc-yate-privado', name: 'OWA', activityUrl: '' },
    cancellationPolicy: { ...yachtTourCancellation, sourceUrl: '' },
    ticketTypes: [
      { id: 'base_group', label: 'Grupo hasta 8 personas', price: 680, min: 1, max: 1, default: 1 },
      { id: 'extra_guest', label: 'Pasajero extra (máx. 4)', price: 45, min: 0, max: 4, default: 0 },
    ],
    timeSlots: ['09:30', '13:30', 'Sunset (verano)'],
    priceUnit: 'grupo privado',
    paymentCurrency: 'EUR',
    bookingEnabled: true,
  },
  jetSkiActivity({
    id: 'gc-jetski-express-30',
    title: 'Jet Ski Tour Express — 30 minutos',
    price: 75,
    duration: '30 minutos',
    shortDescription: 'Primera toma de contacto en moto de agua con briefing, conducción guiada y parada para fotos.',
    description: 'Tour pensado para principiantes: salida guiada, parada para fotos y vídeos y tiempo de conducción libre antes de regresar al puerto.',
    included: [
      'Recogida y regreso al hotel en la zona incluida',
      'Briefing de seguridad',
      'Guía y material necesario',
      'Botella de agua y fruta o chocolatina',
      'Seguro de accidentes y responsabilidad civil',
    ],
    notIncluded: ['Fotos y vídeos: confirmar con el operador; sus páginas publican condiciones distintas', 'Traslado fuera del itinerario previsto'],
  }),
  jetSkiActivity({
    id: 'gc-jetski-aventura-60',
    title: 'Jet Ski Tour Aventura — 60 minutos',
    price: 120,
    duration: '1 hora',
    shortDescription: 'El recorrido favorito por la costa sur, dividido en dos tramos con opción de cambiar de conductor.',
    description: 'Excursión de una hora en moto de agua por la costa de Gran Canaria. El recorrido se divide en dos partes de 30 minutos y los ocupantes pueden cambiar de conductor si cumplen los requisitos.',
    included: [
      'Recogida y regreso al hotel en la zona incluida',
      'Briefing de seguridad',
      'Guía y material necesario',
      'Botella de agua y fruta o chocolatina',
      'Seguro de accidentes y responsabilidad civil',
    ],
    notIncluded: ['Fotos y vídeos: confirmar con el operador; sus páginas publican condiciones distintas', 'Traslado fuera del itinerario previsto'],
  }),
  jetSkiActivity({
    id: 'gc-jetski-explorer-90',
    title: 'Jet Ski Tour Explorer — 90 minutos',
    price: 160,
    duration: '1 h 30 min',
    shortDescription: 'Ruta desde Maspalomas hacia Mogán para descubrir calas, cuevas y rincones de difícil acceso.',
    description: 'Recorrido guiado de nivel intermedio por la costa sur, desde Maspalomas hacia Mogán, con acceso a calas y una cueva marina cuando las condiciones lo permiten.',
    included: [
      'Recogida y regreso al hotel en la zona incluida',
      'Briefing de seguridad',
      'Guía y material necesario',
      'Botella de agua y fruta o chocolatina',
      'Seguro de accidentes y responsabilidad civil',
    ],
    notIncluded: ['Fotos y vídeos: confirmar con el operador; sus páginas publican condiciones distintas', 'Traslado fuera del itinerario previsto'],
  }),
  jetSkiActivity({
    id: 'gc-jetski-tour-120',
    title: 'Jet Ski Tour Gran Canaria — 120 minutos',
    price: 200,
    duration: '2 horas',
    shortDescription: 'La ruta más completa para recorrer paisajes volcánicos y zonas de la costa accesibles solo por mar.',
    description: 'Tour de dos horas por el litoral sur de Gran Canaria, diseñado para combinar navegación, paisaje y adrenalina en zonas a las que solo se puede llegar desde el mar.',
    included: [
      'Recogida y regreso al hotel en la zona incluida',
      'Briefing de seguridad',
      'Guía y material necesario',
      'Botella de agua y fruta o chocolatina',
      'Seguro de accidentes y responsabilidad civil',
    ],
    notIncluded: ['Fotos y vídeos: confirmar con el operador; sus páginas publican condiciones distintas', 'Traslado fuera del itinerario previsto'],
  }),
  legacyExperience({
    id: 'water-taxi-lobos',
    title: 'Isla de Lobos Escape — Water Taxi Experience',
    location: 'Corralejo, Fuerteventura',
    destination: 'fuerteventura',
    category: 'nauticas',
    price: 17,
    duration: '2-3 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: 'https://images.unsplash.com/photo-1696454138713-570f884ba261?auto=format&fit=crop&w=900&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1696454138713-570f884ba261?auto=format&fit=crop&w=1400&q=80',
      'https://images.unsplash.com/photo-1526621480041-c4e2027dfe69?auto=format&fit=crop&w=1400&q=80',
      'https://images.unsplash.com/photo-1571328532572-cb7899a8be7e?auto=format&fit=crop&w=1400&q=80',
    ],
    shortDescription: 'Cruza a la Isla de Lobos en un cómodo water taxi y explora una reserva natural de aguas turquesas.',
    description: 'Descubre uno de los rincones más vírgenes del archipiélago canario. Nuestro water taxi te lleva directamente desde Corralejo hasta la mágica Isla de Lobos, donde tendrás tiempo libre para nadar, hacer snorkel y disfrutar de la reserva natural.',
    included: ['Trayecto en barco ida y vuelta', 'Chaleco salvavidas', 'Guía a bordo', 'Seguro de actividad'],
    notIncluded: ['Comida y bebidas', 'Equipo de snorkel (alquiler disponible)'],
    meetingPoint: 'Puerto de Corralejo, muelle norte',
    pickup: 'Presentacion directa en el muelle. Check-in 20 minutos antes.',
    groupSize: 'Hasta 12 pers.',
    providerName: 'OWA Lobos Partner',
    cancellationShort: 'Cancelacion gratuita hasta 24 horas antes.',
    restrictions: [
      'Llevar agua, proteccion solar y calzado comodo.',
      'La autorizacion de acceso a la isla puede ser necesaria segun la temporada.',
    ],
    ticketTypes: [
      { id: 'adult', label: 'Adultos', price: 17, min: 1, max: 12, default: 2, seats: 1 },
      { id: 'child', label: 'Ninos', price: 17, min: 0, max: 12, default: 0, seats: 1 },
    ],
  }),
  legacyExperience({
    id: 'ocean-giants-cruise',
    title: 'Ocean Giants: Atlantic Wildlife Cruise — Gran Canaria',
    location: 'Puerto Rico, Gran Canaria',
    destination: 'gran-canaria',
    category: 'naturaleza',
    price: 40,
    originalPrice: 55,
    duration: '4 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: 'https://images.unsplash.com/photo-1518399681705-1c1a55e5e883?auto=format&fit=crop&w=900&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1518399681705-1c1a55e5e883?auto=format&fit=crop&w=1400&q=80',
      'https://images.unsplash.com/photo-1568430462989-44163eb1752f?auto=format&fit=crop&w=1400&q=80',
    ],
    shortDescription: 'Avistamiento de ballenas y delfines en su hábitat natural con guía biólogo marino.',
    description: 'Embárcate en una expedición de 4 horas por las aguas del Atlántico frente a Gran Canaria. Con más del 98% de éxito en avistamientos, es una experiencia inolvidable para toda la familia.',
    included: ['Bebidas y snacks a bordo', 'Guía biólogo marino', 'Baño en cala secreta', 'Seguro completo'],
    notIncluded: ['Traslado al puerto', 'Propinas'],
    meetingPoint: 'Puerto Rico Marina, Gran Canaria',
    groupSize: 'Grupo reducido',
    providerName: 'OWA Marine Wildlife Partner',
    cancellationShort: 'Cancelacion gratuita hasta 24 horas antes.',
    restrictions: ['Actividad sujeta a condiciones del mar y fauna salvaje.', 'No se garantizan avistamientos al 100%.'],
    ticketTypes: [
      { id: 'adult', label: 'Adultos', price: 40, min: 1, max: 12, default: 2, seats: 1 },
      { id: 'child', label: 'Ninos', price: 30, min: 0, max: 12, default: 0, seats: 1 },
    ],
  }),
  legacyExperience({
    id: 'safari-buggy',
    title: 'Safari Buggy — Barrancos Volcánicos y Cañones del Sur',
    location: 'Maspalomas, Gran Canaria',
    destination: 'gran-canaria',
    category: 'aventura',
    price: 95,
    duration: '2.5 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: 'https://images.unsplash.com/photo-1704287824391-b3bc1e04ea33?auto=format&fit=crop&w=900&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1704287824391-b3bc1e04ea33?auto=format&fit=crop&w=1400&q=80',
      'https://images.unsplash.com/photo-1704287994766-3d76e0ca3264?auto=format&fit=crop&w=1400&q=80',
      'https://images.unsplash.com/photo-1698245674677-6267b312d29d?auto=format&fit=crop&w=1400&q=80',
    ],
    shortDescription: 'Conduce un buggy por barrancos volcánicos, cañones y miradores del sur de Gran Canaria.',
    description: 'Vive una aventura off-road única atravesando paisajes volcánicos, cañones ancestrales y miradores impresionantes. Ideal para quienes buscan adrenalina y descubrir la cara salvaje de Gran Canaria.',
    included: ['Buggy 2 plazas', 'Casco y gafas', 'Guía en ruta', 'Fotos incluidas'],
    notIncluded: ['Combustible (aprox 15€)', 'Almuerzo'],
    meetingPoint: 'Base OWA Maspalomas',
    groupSize: 'Hasta 2 pers. por buggy',
    providerName: 'OWA Buggy Partner',
    restrictions: ['Permiso de conducir B requerido para conducir.', 'Calzado cerrado recomendado.'],
    priceUnit: 'buggy',
    ticketTypes: [
      { id: 'buggy', label: 'Buggy 2 plazas', price: 95, min: 1, max: 6, default: 1, seats: 2 },
    ],
  }),
  legacyExperience({
    id: 'jet-ski-tenerife',
    title: 'Moto de Agua & Circuito Costero — Tenerife Sur',
    location: 'Los Cristianos, Tenerife',
    destination: 'tenerife',
    category: 'nauticas',
    price: 65,
    originalPrice: 80,
    duration: '1 hora',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1564633351631-e85bd59a91af?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1564633351631-e85bd59a91af?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Pilota una moto de agua Yamaha por la costa sur de Tenerife.',
    description: 'Siente la velocidad y libertad pilotando una moto de agua última generación. Recorre acantilados, playas escondidas y descubre la costa sur de Tenerife como nunca.',
    included: ['Moto de agua Yamaha VX', 'Instructor', 'Chaleco salvavidas', 'Combustible'],
    notIncluded: ['Fotos (opcional 15€)'],
    meetingPoint: 'Marina Los Cristianos',
    groupSize: 'Hasta 2 pers. por moto',
    providerName: 'OWA Tenerife Jet Ski Partner',
    restrictions: ['Edad minima 16 anos para conducir segun operador.', 'Actividad sujeta al estado del mar.'],
    priceUnit: 'moto',
    ticketTypes: [
      { id: 'single_jet_ski', label: 'Moto individual', price: 65, min: 1, max: 6, default: 1, seats: 1 },
      { id: 'double_jet_ski', label: 'Moto doble', price: 80, min: 0, max: 6, default: 0, seats: 2 },
    ],
  }),
  legacyExperience({
    id: 'parasailing-lanzarote',
    title: 'Parasailing sobre Playa Blanca — Vuelo Panorámico',
    location: 'Playa Blanca, Lanzarote',
    destination: 'lanzarote',
    category: 'aire',
    price: 55,
    duration: '45 minutos',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1560419656-c2fe828696af?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1560419656-c2fe828696af?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Vuela a 80 metros sobre el mar con vistas a los Volcanes de Timanfaya.',
    description: 'El parasailing es la mejor forma de admirar Lanzarote desde las alturas. Vuela solo, en pareja o incluso trío sobre las aguas cristalinas frente a Playa Blanca.',
    included: ['Vuelo 15-20 min', 'Equipo homologado', 'Traslado en lancha', 'Foto digital'],
    notIncluded: ['Video HD (opcional 20€)'],
    meetingPoint: 'Muelle deportivo Playa Blanca',
    providerName: 'OWA Parasailing Lanzarote Partner',
    restrictions: ['Peso y meteorologia sujetos a validacion del operador.'],
  }),
  legacyExperience({
    id: 'banana-boat',
    title: 'Banana Boat & Sofá Volador — Diversión en Familia',
    location: 'Corralejo, Fuerteventura',
    destination: 'fuerteventura',
    category: 'nauticas',
    price: 25,
    duration: '30 minutos',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1505738313577-5357ff512f16?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1505738313577-5357ff512f16?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Diversión garantizada para toda la familia sobre la clásica banana hinchable.',
    description: 'Un clásico de las vacaciones. Sube a la banana boat con hasta 6 personas y déjate arrastrar por lanchas rápidas por la costa de Corralejo. Risas garantizadas.',
    included: ['30 min de recorrido', 'Chaleco salvavidas', 'Instructor'],
    notIncluded: ['Fotos'],
    meetingPoint: 'Playa de Corralejo Viejo',
    providerName: 'OWA Fun Rides Partner',
    restrictions: ['Edad minima segun condiciones del operador y estado del mar.'],
  }),
  legacyExperience({
    id: 'boat-rental',
    title: 'Alquiler de Barco sin Licencia — Día Completo',
    location: 'Puerto Mogán, Gran Canaria',
    destination: 'gran-canaria',
    category: 'nauticas',
    price: 180,
    originalPrice: 220,
    duration: '8 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: 'https://images.unsplash.com/photo-1571328532572-cb7899a8be7e?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1571328532572-cb7899a8be7e?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Alquila un barco de recreo sin licencia y navega libremente por la costa sur.',
    description: 'Vive un día en el mar a tu ritmo. Barcos modernos de 40cv sin licencia, aptos para hasta 6 personas. Ideal para calas, snorkel y baño en aguas turquesas.',
    included: ['Barco hasta 6 pers.', 'Combustible incluido', 'Nevera con hielo', 'Ancla y equipo náutico'],
    notIncluded: ['Comida', 'Snorkel (alquiler 5€)'],
    meetingPoint: 'Puerto Mogán',
    groupSize: 'Hasta 6 pers.',
    providerName: 'OWA Boat Rental Partner',
    priceUnit: 'barco',
    ticketTypes: [
      { id: 'boat', label: 'Barco completo', price: 180, min: 1, max: 2, default: 1, seats: 6 },
    ],
    restrictions: ['Se requiere documentacion valida del responsable de la salida.', 'Condiciones sujetas a fianza y meteorologia.'],
  }),
  legacyExperience({
    id: 'catamaran-sunset',
    title: 'Catamarán al Atardecer con Cena a Bordo',
    location: 'Costa Adeje, Tenerife',
    destination: 'tenerife',
    category: 'nauticas',
    price: 75,
    duration: '3 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: 'https://images.unsplash.com/photo-1564555449603-b0eefe1b9094?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1564555449603-b0eefe1b9094?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Vive el atardecer más romántico navegando en catamarán con cena y barra libre.',
    description: 'Navega frente a los acantilados de Los Gigantes mientras el sol se pone. Incluye cena, barra libre de bebidas locales y música en directo.',
    included: ['Cena buffet', 'Barra libre', 'DJ a bordo', 'Baño en cala'],
    notIncluded: ['Traslado'],
    meetingPoint: 'Puerto Colón, Costa Adeje',
    groupSize: 'Catamaran compartido',
    providerName: 'OWA Sunset Catamaran Partner',
    cancellationNoticeHours: 48,
    cancellationShort: 'Cancelacion gratuita hasta 48 horas antes.',
    ticketTypes: [
      { id: 'adult', label: 'Adultos', price: 75, min: 1, max: 24, default: 2, seats: 1 },
      { id: 'child', label: 'Ninos', price: 55, min: 0, max: 24, default: 0, seats: 1 },
    ],
  }),
  legacyExperience({
    id: 'snorkel-teno',
    title: 'Snorkel en Reserva Marina de Teno',
    location: 'Los Gigantes, Tenerife',
    destination: 'tenerife',
    category: 'buceo',
    price: 45,
    duration: '3 horas',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1524482131769-b23c0f633d7c?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1524482131769-b23c0f633d7c?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Descubre la biodiversidad marina en la reserva protegida de Teno.',
    description: 'Bucea con tortugas, peces loro, morenas y bancos de sardinas en una de las reservas marinas mejor conservadas de Canarias.',
    included: ['Equipo completo de snorkel', 'Instructor titulado', 'Traslado en lancha', 'Bebida'],
    notIncluded: ['Fotos submarinas'],
    meetingPoint: 'Puerto Los Gigantes',
    providerName: 'OWA Snorkel Tenerife Partner',
    restrictions: ['Saber nadar es obligatorio.'],
  }),
  legacyExperience({
    id: 'buceo-lanzarote',
    title: 'Bautismo de Buceo en Arrecifes Volcánicos',
    location: 'Puerto del Carmen, Lanzarote',
    destination: 'lanzarote',
    category: 'buceo',
    price: 80,
    duration: '3 horas',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1658298208155-ab71765747a1?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1658298208155-ab71765747a1?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Tu primera inmersión de buceo con instructor PADI, sin experiencia previa.',
    description: 'Sumérgete hasta 6m con instructor PADI en formaciones volcánicas y cuevas submarinas únicas del atlántico canario.',
    included: ['Equipo completo de buceo', 'Instructor PADI', 'Seguro de buceo', 'Certificado PADI Discover'],
    notIncluded: ['Traslado', 'Fotos'],
    meetingPoint: 'Centro de buceo Puerto del Carmen',
    providerName: 'OWA Diving Lanzarote Partner',
    restrictions: ['No apto para viajar en avion el mismo dia.', 'Cuestionario medico requerido antes de bucear.'],
  }),
  legacyExperience({
    id: 'kayak-cuevas',
    title: 'Kayak & Cuevas Marinas — Mogán',
    location: 'Puerto Mogán, Gran Canaria',
    destination: 'gran-canaria',
    category: 'nauticas',
    price: 35,
    duration: '3 horas',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1620903669944-de50fbe78210?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1620903669944-de50fbe78210?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Explora cuevas marinas y calas ocultas remando en kayak transparente.',
    description: 'Recorre acantilados vírgenes, cuevas marinas y bahías escondidas remando en kayak transparente con guía experto.',
    included: ['Kayak individual/doble', 'Chaleco', 'Snorkel', 'Guía + fotos'],
    notIncluded: ['Bebidas'],
    meetingPoint: 'Playa de Mogán',
    providerName: 'OWA Kayak Mogán Partner',
    restrictions: ['Actividad sujeta al estado del mar y nivel fisico basico.'],
  }),
  legacyExperience({
    id: 'teide-hike',
    title: 'Ascenso al Teide al Amanecer + Estrellas',
    location: 'Parque Nacional del Teide, Tenerife',
    destination: 'tenerife',
    category: 'aventura',
    price: 110,
    originalPrice: 130,
    duration: '10 horas',
    rating: null,
    reviews: 0,
    featured: true,
    image: 'https://images.unsplash.com/photo-1616628950295-d3288bd7a96d?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1616628950295-d3288bd7a96d?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Sube al pico más alto de España al amanecer y observa las estrellas antes.',
    description: 'Una experiencia única: observación astronómica desde 2.400m, cena en refugio de montaña, ascenso nocturno al Teide y amanecer sobre el mar de nubes.',
    included: ['Guía de montaña', 'Cena', 'Permiso Teide', 'Telescopio', 'Traslado'],
    notIncluded: ['Equipo personal'],
    meetingPoint: 'Punto encuentro Puerto de la Cruz',
    groupSize: 'Grupo guiado',
    providerName: 'OWA Teide Trek Partner',
    cancellationNoticeHours: 48,
    cancellationShort: 'Cancelacion gratuita hasta 48 horas antes.',
    restrictions: ['No apto para personas con problemas cardiacos o de altura.', 'Ropa de abrigo obligatoria.'],
  }),
  legacyExperience({
    id: 'paragliding',
    title: 'Parapente Biplaza sobre Costa Sur',
    location: 'Ifonche, Tenerife',
    destination: 'tenerife',
    category: 'aire',
    price: 120,
    duration: '1.5 horas',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1620649332832-45f944292179?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1620649332832-45f944292179?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Vuelo tándem en parapente sobre acantilados y mar con piloto certificado.',
    description: 'Despega desde 800m en Ifonche y aterriza en la playa. Vuelo de 20-25 min con piloto profesional. Video HD incluido.',
    included: ['Piloto certificado', 'Equipo completo', 'Video HD + fotos', 'Traslado despegue'],
    notIncluded: ['Traslado desde hotel'],
    meetingPoint: 'Base Ifonche, Adeje',
    providerName: 'OWA Paragliding Tenerife Partner',
    restrictions: ['Peso y viento sujetos a validacion del piloto.', 'Calzado cerrado obligatorio.'],
  }),
  legacyExperience({
    id: 'stand-up-paddle',
    title: 'Stand Up Paddle al Atardecer',
    location: 'Sotavento, Fuerteventura',
    destination: 'fuerteventura',
    category: 'nauticas',
    price: 30,
    duration: '2 horas',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1613857794632-052edbb98d58?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1613857794632-052edbb98d58?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Sesión guiada de SUP en la laguna de Sotavento al atardecer.',
    description: 'Aprende SUP en las aguas tranquilas de la laguna de Sotavento, uno de los mejores spots del mundo, con guía profesional.',
    included: ['Tabla SUP', 'Traje neopreno', 'Instructor', 'Fotos'],
    notIncluded: ['Traslado'],
    meetingPoint: 'Playa Sotavento',
    providerName: 'OWA SUP Fuerteventura Partner',
  }),
  legacyExperience({
    id: 'wine-tour',
    title: 'Ruta del Vino & Bodegas Volcánicas',
    location: 'La Geria, Lanzarote',
    destination: 'lanzarote',
    category: 'gastronomia',
    price: 60,
    duration: '5 horas',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1575450411797-d1aed117d091?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1575450411797-d1aed117d091?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'Cata de vinos volcánicos con visita a 3 bodegas históricas.',
    description: 'Descubre el sabor único de los vinos malvasía cultivados en suelo volcánico. Visita 3 bodegas familiares con maridaje canario.',
    included: ['Traslado', 'Guía', '3 bodegas', 'Cata + tapas'],
    notIncluded: ['Compras adicionales'],
    meetingPoint: 'Playa Blanca / Puerto del Carmen',
    groupSize: 'Grupo degustacion',
    providerName: 'OWA Wine Lanzarote Partner',
  }),
  legacyExperience({
    id: 'stargazing',
    title: 'Observación de Estrellas — Cielos La Palma',
    location: 'Roque de los Muchachos, La Palma',
    destination: 'la-palma',
    category: 'naturaleza',
    price: 50,
    duration: '3 horas',
    rating: null,
    reviews: 0,
    image: 'https://images.unsplash.com/photo-1488330890490-c291ecf62571?auto=format&fit=crop&w=900&q=80',
    gallery: ['https://images.unsplash.com/photo-1488330890490-c291ecf62571?auto=format&fit=crop&w=1400&q=80'],
    shortDescription: 'La Palma es Reserva Starlight: observa la Vía Láctea desde el mejor cielo del mundo.',
    description: 'Con guía astrónomo y telescopio profesional observarás planetas, galaxias y nebulosas desde el Roque de los Muchachos, uno de los mejores puntos de observación del planeta.',
    included: ['Telescopio profesional', 'Astrónomo guía', 'Manta térmica', 'Chocolate caliente'],
    notIncluded: ['Traslado'],
    meetingPoint: 'El Paso, La Palma',
    providerName: 'OWA Stargazing La Palma Partner',
    restrictions: ['Abrigo recomendado incluso en verano.', 'Actividad sujeta a nubosidad y viento.'],
  }),
  // Actividades verificadas de Gran Canaria aportadas por el catálogo Skywork
  // (confirmación por WhatsApp hasta contrato operativo / disponibilidad en vivo).
  ...additionalActivities,
];
